import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import type { NotificationType } from '@/hooks/useNotificationPreferences';

export interface Notification {
  id: string;
  user_id: string;
  type: 'lead' | 'todo' | 'ticket' | 'project' | 'project_note' | 'location' | 'project_status' | 'project_delivered' | 'feedback';
  title: string;
  description: string | null;
  reference_id: string | null;
  reference_table: string | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
}

interface NotificationContextType {
  unreadCount: number;
  notifications: Notification[];
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const LOG_PREFIX = '[NotificationContext]';

/**
 * Eén realtime-abonnement, declaratief beschreven.
 *
 * De `precondition` is het hele punt van deze structuur: een kanaal gaat pas
 * open als het iets kan opleveren, en een kanaal dat dicht blijft zegt in de
 * console waarom. Voorheen ging elk kanaal onvoorwaardelijk open en was een
 * kanaal dat nooit iets ontving in de log niet te onderscheiden van een kanaal
 * dat werkte.
 */
interface ChannelSpec {
  /** Kanaalnaam richting Supabase. */
  name: string;
  /** Tabel waarop dit kanaal luistert. */
  table: string;
  /** Optioneel postgres_changes-filter, bijvoorbeeld `user_id=eq.<id>`. */
  filter?: string;
  /**
   * Wat er gebeurt bij een binnengekomen INSERT. supabase-js exporteert het
   * payload-type niet op dit importpad, vandaar de bewuste uitzondering.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (payload: any) => void | Promise<void>;
  /**
   * Beslist of het kanaal opengaat. Ontbreekt hij, dan gaat het kanaal altijd
   * open. De reden wordt gelogd, ook als het antwoord "ja" is.
   */
  precondition?: () => Promise<{ open: boolean; reason: string }>;
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();

  // Load unread count on mount
  const loadUnreadCount = async () => {
    if (!user) return;
    
    const { count } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    
    setUnreadCount(count || 0);
  };

  // Check notification preferences
  const checkNotificationPreferences = async (type: NotificationType) => {
    if (!user) return { inapp: true, push: true };

    try {
      const { data } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (!data) return { inapp: true, push: true };

      // Check quiet hours for push
      let allowPush = data[`${type}_push` as keyof typeof data] as boolean;
      if (allowPush && data.quiet_hours_enabled) {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        
        const [startHour, startMin] = data.quiet_hours_start.split(':').map(Number);
        const [endHour, endMin] = data.quiet_hours_end.split(':').map(Number);
        
        const startTime = startHour * 60 + startMin;
        const endTime = endHour * 60 + endMin;
        
        // Handle overnight quiet hours
        if (startTime > endTime) {
          if (currentTime >= startTime || currentTime < endTime) {
            allowPush = false;
          }
        } else {
          if (currentTime >= startTime && currentTime < endTime) {
            allowPush = false;
          }
        }
      }

      // A type without its own preference column (such as 'feedback', ADR 0021) is shown in the
      // app; web push stays off for it (the iPhone gets its own push via apns-push).
      const inapp = data[`${type}_inapp` as keyof typeof data] as boolean | undefined;
      return {
        inapp: inapp === undefined ? true : inapp,
        push: allowPush === undefined ? false : allowPush,
        sound: data.sound_enabled as boolean,
        vibration: data.vibration_enabled as boolean,
      };
    } catch (error) {
      console.error('Error checking preferences:', error);
      return { inapp: true, push: true };
    }
  };

  // Save notification to database
  const saveNotification = async (notification: Omit<Notification, 'id' | 'user_id' | 'created_at' | 'read_at'>) => {
    if (!user) return;
    
    // Check preferences
    const prefs = await checkNotificationPreferences(notification.type as NotificationType);
    
    // Skip if in-app notifications disabled
    if (!prefs.inapp) {
      console.log(`In-app notification blocked by preferences: ${notification.type}`);
      return;
    }
    
    const { data, error } = await supabase.from('notifications').insert({
      user_id: user.id,
      ...notification,
    }).select().single();

    // Trigger push notification if enabled in preferences
    if (data && !error && prefs.push) {
      try {
        await supabase.functions.invoke('push-notifications', {
          body: {
            userId: user.id,
            title: notification.title,
            body: notification.description || '',
            url: `/crm/${notification.reference_table || 'notifications'}`,
            data: {
              type: notification.type,
              referenceId: notification.reference_id,
              referenceTable: notification.reference_table,
            },
          },
        });
      } catch (pushError) {
        // Silent fail - push notifications are optional enhancement
        console.error('Push notification failed:', pushError);
      }
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast({ title: 'Fout bij markeren als gelezen', variant: 'destructive', duration: 3000 });
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      toast({ title: 'Fout bij markeren als gelezen', variant: 'destructive', duration: 3000 });
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    try {
      const { data } = await supabase
        .from('notifications')
        .select('is_read')
        .eq('id', notificationId)
        .single();

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      if (data && !data.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast({ title: 'Fout bij verwijderen notificatie', variant: 'destructive', duration: 3000 });
    }
  };

  // Refresh notifications list
  const refreshNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (data) {
        setNotifications(data as Notification[]);
      }
    } catch (error) {
      console.error('Failed to refresh notifications:', error);
    }
  };

  useEffect(() => {
    loadUnreadCount();
    refreshNotifications();
  }, [user]);

  // ---------------------------------------------------------------------------
  // Realtime-kanalen — één registry, elk kanaal met een voorwaarde en een melding
  //
  // Gemeten op 2026-09-08 tegen project fxlorumfwzrdwiftbalo (alleen SELECT):
  //
  //  notifications  1.531 inserts, staat in publicatie `supabase_realtime`.
  //                 Werkt, en is het enige kanaal dat aantoonbaar levert.
  //  project_notes     41 inserts (32 rijen over), staat in de publicatie. Leek
  //                 te werken — er kwamen twee notificaties van 2026-04-08 uit
  //                 deze handler — maar dat waren de enige: 31 van de 32 notities
  //                 bereikten niemand, omdat de handler de melding zélf aanmaakte
  //                 en dus alleen vuurde als er toevallig iemand keek. Sinds
  //                 migratie 071 doet de trigger notify_new_project_note() dat,
  //                 en is dit kanaal hier weggehaald.
  //  tickets            0 inserts sinds de tabel bestaat (21-08-2025, migratie
  //                 20250821075908). Staat wél in de publicatie (migratie
  //                 20251027133838 regel 11), dus elke ingelogde gebruiker in elk
  //                 portaal opende hier een permanente websocket-inschrijving op
  //                 een tabel die in 453 dagen geen enkele rij kreeg. Nul
  //                 notificaties van type 'ticket' in de database.
  //  unified_todos    195 inserts, maar de tabel staat NIET in de publicatie
  //                 `supabase_realtime` — die bevat `todos`, niet `unified_todos`
  //                 (migratie 20251027133838 regel 10). Dit kanaal meldde al die
  //                 tijd SUBSCRIBED en ontving nul events: nul notificaties dragen
  //                 de titel van deze handler ('✅ Nieuwe Taak'), alle twintig
  //                 todo-notificaties komen van de databasetrigger
  //                 `on_todo_assignment` ('📋 Nieuwe taak toegewezen'). Die
  //                 trigger schrijft in `notifications`, en het kanaal daarop
  //                 levert de toast dus al. Zou `unified_todos` alsnog aan de
  //                 publicatie worden toegevoegd, dan schrijft deze handler een
  //                 tweede notificatierij voor dezelfde taak.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!user) return;

    // `cancelled` is nodig omdat een kanaal pas na een await opengaat: unmount
    // (of een user-wissel) kan tussen de voorwaarde en het openen vallen.
    let cancelled = false;
    const openChannels: ReturnType<typeof supabase.channel>[] = [];

    // Een kanaal dat niet luistert zag er in de log hetzelfde uit als een kanaal
    // dat wél luistert — beide werden met console.log weggeschreven. Alles wat
    // geen SUBSCRIBED is, is vanaf hier een fout, zodat het volgende stille geval
    // zich meldt. Statussen ná opruimen (CLOSED) negeren we; die horen erbij.
    const reportStatus = (spec: ChannelSpec, status: string) => {
      if (cancelled) return;
      if (status === 'SUBSCRIBED') {
        console.info(`${LOG_PREFIX} kanaal open op ${spec.table} (${spec.name})`);
        return;
      }
      console.error(
        `${LOG_PREFIX} kanaal ${spec.name} op ${spec.table} is NIET actief (status ${status}). ` +
        'Realtime-updates voor deze tabel komen niet aan.'
      );
    };

    /**
     * De ticketmodule heeft nooit gedraaid (0 rijen, gemeten 2026-09-08). Zolang
     * dat zo is heeft niemand iets aan het kanaal. Zodra het eerste ticket
     * bestaat gaat het bij de eerstvolgende sessie vanzelf weer open.
     *
     * De telling doet meteen dienst als rechtencontrole: de enige policy op
     * `tickets` is `admin_all_tickets` (admin/super_admin), dus een charging_user,
     * inspector of client krijgt 0 terug en opende voorheen een kanaal waarlangs
     * onder RLS toch nooit een rij zou komen.
     */
    const ticketModuleInUse = async () => {
      const { count, error } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true });

      if (error) {
        return { open: false, reason: `telling op tickets mislukte: ${error.message}` };
      }
      if (!count) {
        return {
          open: false,
          reason: 'ticketmodule nog nooit gebruikt (0 rijen); kanaal gaat open zodra het eerste ticket bestaat',
        };
      }
      return { open: true, reason: `${count} zichtbare tickets` };
    };

    const specs: ChannelSpec[] = [
      {
        name: 'notifications-realtime',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
        handler: async (payload) => {
          console.log('[NotificationContext] Notification realtime INSERT event received:', payload);
          const newNotification = payload.new as Notification;

          // Check preferences
          const prefs = await checkNotificationPreferences(newNotification.type as NotificationType);

          if (!prefs.inapp) {
            console.log(`[NotificationContext] Real-time notification blocked by preferences: ${newNotification.type}`);
            return;
          }

          // Update unread count
          setUnreadCount(prev => prev + 1);

          // Show toast notification
          toast({
            title: newNotification.title,
            description: newNotification.description || '',
            duration: 6000
          });

          // Vibrate if enabled
          if ('vibrate' in navigator && prefs.vibration && newNotification.type === 'lead') {
            navigator.vibrate([200, 100, 200]);
          }

          // Trigger push notification if enabled
          if (prefs.push) {
            try {
              await supabase.functions.invoke('push-notifications', {
                body: {
                  userId: user.id,
                  title: newNotification.title,
                  body: newNotification.description || '',
                  url: `/crm/${newNotification.reference_table || 'notifications'}`,
                  data: {
                    type: newNotification.type,
                    referenceId: newNotification.reference_id,
                    referenceTable: newNotification.reference_table,
                  },
                },
              });
            } catch (pushError) {
              console.error('Push notification failed:', pushError);
            }
          }

          // Refresh notifications list
          refreshNotifications();
        },
      },
      {
        name: 'todos-notifications',
        table: 'unified_todos',
        filter: `assigned_to=eq.${user.id}`,
        // Dit kanaal blijft dicht, met opzet. Zie de meting bovenaan dit blok:
        // `unified_todos` zit niet in de publicatie, dus deze handler heeft nooit
        // gedraaid, en de databasetrigger `on_todo_assignment` maakt de
        // notificatie al aan. Weghalen van deze precondition zonder de handler
        // aan te passen levert dubbele notificaties op zodra iemand
        // `ALTER PUBLICATION supabase_realtime ADD TABLE public.unified_todos`
        // draait.
        precondition: async () => ({
          open: false,
          reason:
            'unified_todos staat niet in publicatie supabase_realtime (gemeten 2026-09-08) en ' +
            'databasetrigger on_todo_assignment schrijft deze notificatie al',
        }),
        handler: async (payload) => {
          console.log('[NotificationContext] Todo realtime INSERT event received:', payload);

          // Allow all todo notifications (self-assignment check removed for better testing and UX)

          const notification = {
            type: 'todo' as const,
            title: '✅ Nieuwe Taak',
            description: payload.new.title || 'Je hebt een nieuwe taak gekregen',
            reference_id: payload.new.id,
            reference_table: 'unified_todos',
            is_read: false,
          };

          console.log('[NotificationContext] Creating todo notification...');
          await saveNotification(notification);
          setUnreadCount(prev => prev + 1);

          console.log('[NotificationContext] Todo notification created successfully');
          toast({
            title: notification.title,
            description: notification.description,
            duration: 5000
          });
        },
      },
      {
        name: 'tickets-notifications',
        table: 'tickets',
        precondition: ticketModuleInUse,
        handler: async (payload) => {
          // Customer-app intake routes notifications atomically on the server.
          // A browser must not create another ping or invent its recipients.
          if (payload.new.source === 'customer_app') return;
          console.log('[NotificationContext] Ticket realtime INSERT event received:', payload);

          const notification = {
            type: 'ticket' as const,
            title: '🎫 Nieuw Ticket',
            description: payload.new.title || 'Er is een nieuw ticket aangemaakt',
            reference_id: payload.new.id,
            reference_table: 'tickets',
            is_read: false,
          };

          console.log('[NotificationContext] Creating ticket notification...');
          await saveNotification(notification);
          setUnreadCount(prev => prev + 1);

          console.log('[NotificationContext] Ticket notification created successfully');
          toast({
            title: notification.title,
            description: notification.description,
            duration: 5000
          });
        },
      },
      // Het kanaal `project_notes` stond hier tot 8 september 2026, en het
      // maakte de melding zelf aan met saveNotification(). Daardoor bestond een
      // melding alleen als er toevallig iemand met de app open keek: gemeten 32
      // notities, 2 meldingen, 31 notities die niemand bereikten. De enige die
      // het wél haalde leverde twee rijen op, één per ingelogde beheerder.
      //
      // Migratie 071 legt dat in de database (notify_new_project_note), zoals
      // 068 dat voor tickets deed en januari voor de to-do's. De spec is hier
      // wéggehaald en niet alleen uitgezet: liet je hem staan, dan schreef hij
      // vanaf nu een tweede rij bovenop die van de trigger.
      //
      // Er gaat niets verloren. De rij van de trigger komt binnen op het kanaal
      // `notifications` hierboven, dat gefilterd is op user_id=eq.<mij> en
      // teller, toast, pushmelding en verversing al doet. Alleen de trilling bij
      // een notitie van het type `issue` vervalt: de notificatierij draagt het
      // note_type niet, en op de emoji in de titel matchen is te broos.
    ];

    const openChannel = (spec: ChannelSpec) => {
      const channel = supabase
        .channel(spec.name)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: spec.table,
            ...(spec.filter ? { filter: spec.filter } : {}),
          },
          spec.handler
        )
        .subscribe((status) => reportStatus(spec, status));

      openChannels.push(channel);
    };

    console.log(`${LOG_PREFIX} Setting up realtime subscriptions for user:`, user.id);

    const opened: string[] = [];
    const skipped: string[] = [];

    // Kanalen zonder voorwaarde gaan meteen open, zodat `notifications` — het
    // enige kanaal dat aantoonbaar levert — niet hoeft te wachten op een
    // netwerkrondje voor een ander kanaal.
    specs
      .filter((spec) => !spec.precondition)
      .forEach((spec) => {
        openChannel(spec);
        opened.push(spec.table);
      });

    Promise.all(
      specs
        .filter((spec) => spec.precondition)
        .map(async (spec) => {
          let verdict: { open: boolean; reason: string };

          try {
            verdict = await spec.precondition!();
          } catch (error) {
            verdict = {
              open: false,
              reason: `voorwaarde wierp een fout: ${(error as Error)?.message ?? error}`,
            };
          }

          // Na de await kan de provider al opgeruimd zijn.
          if (cancelled) return;

          if (!verdict.open) {
            skipped.push(`${spec.table} (${verdict.reason})`);
            return;
          }

          // Vanaf hier geen await meer: het kanaal staat in `openChannels`
          // voordat de cleanup opnieuw aan de beurt kan komen.
          openChannel(spec);
          opened.push(`${spec.table} (${verdict.reason})`);
        })
    ).then(() => {
      if (cancelled) return;
      console.info(
        `${LOG_PREFIX} realtime actief op: ${opened.join(', ') || 'geen enkele tabel'}` +
        (skipped.length ? ` — overgeslagen: ${skipped.join(' | ')}` : '')
      );
    });

    return () => {
      cancelled = true;
      console.log(`${LOG_PREFIX} Cleaning up ${openChannels.length} realtime subscriptions`);
      openChannels.forEach((channel) => supabase.removeChannel(channel));
    };
  }, [user, toast]);

  return (
    <NotificationContext.Provider value={{ 
      unreadCount, 
      notifications,
      markAsRead, 
      markAllAsRead,
      deleteNotification,
      refreshNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
